// Màn hình "KPI Khiếu nại" — phiếu phạt khiếu nại 3 nguồn (Google/Zalo/Chat) từ
// ops-quality: tổng quan tháng → trend 12 tháng → chất lượng xử lý Chat (SLA,
// gate, gỡ phạt) → chi tiết từng phiếu. View-only.
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { ChartBox, chartDataset } from '../components/ChartBox';
import type { OpsComplaintsResponse } from '../types';
import { asNumber, complaintLevelLabel, complaintLevelTone, fmtRemain, money, monthLabel, num, pct1, penaltySourceLabel, penaltyStatusLabel, penaltyStatusTone, remainTone, uniq } from '../utils';

interface OpsComplaintsScreenProps {
  data: OpsComplaintsResponse | null;
  loaded: boolean;
  busy: boolean;
  month: string;
  onMonth: (month: string) => void;
}

const SOURCE_COLORS: Record<string, string> = { google_review: '#2563eb', zalo_review: '#0891b2', chat_complaint: '#7c3aed' };
const slaTone = (v: number): 'green' | 'amber' | 'red' => (v >= 90 ? 'green' : v >= 70 ? 'amber' : 'red');

export function OpsComplaintsScreen({ data, loaded, busy, month, onMonth }: OpsComplaintsScreenProps) {
  if (busy && !loaded) return <div className="card empty">Đang tải KPI khiếu nại...</div>;
  if (!loaded) return <div className="card empty">Mở mục KPI Khiếu nại để tải dữ liệu.</div>;

  const months = data?.meta?.months || [];
  const summary = data?.summary || [];
  const trend = data?.trend || [];
  const chatMonthly = data?.chatMonthly || [];
  const chatDept = data?.chatDept || [];
  const chatDaily = data?.chatDaily || [];
  const details = data?.details || [];
  const slaGate1 = data?.slaGate1 || [];
  const slaBreached = data?.slaBreached || [];
  const slaExplOpen = data?.slaExplOpen || [];
  const slaReviewQueue = data?.slaReviewQueue || [];
  const explOverdue = slaExplOpen.filter(r => asNumber(r.remaining_h) < 0).length;
  const gate1Nearest = slaGate1.length ? slaGate1[0].remaining_h : null;

  const total = summary.reduce((a, r) => a + asNumber(r.cnt), 0);
  const confirmed = summary.filter(r => r.penalty_status === 'confirmed').reduce((a, r) => a + asNumber(r.cnt), 0);
  const confirmedAmount = summary.filter(r => r.penalty_status === 'confirmed').reduce((a, r) => a + asNumber(r.amount), 0);
  const waived = summary.filter(r => r.penalty_status === 'waived').reduce((a, r) => a + asNumber(r.cnt), 0);
  const pending = summary.filter(r => r.penalty_status === 'pending').reduce((a, r) => a + asNumber(r.cnt), 0);
  const chatNow = chatMonthly.find(r => r.metric_month === month);
  const sla24 = chatNow?.sla_gate1_within_24h != null ? asNumber(chatNow.sla_gate1_within_24h) * 100 : null;
  const grr = chatNow?.good_resolution_rate != null ? asNumber(chatNow.good_resolution_rate) * 100 : null;

  // Bảng nguồn × trạng thái trong tháng.
  const sources = uniq(summary.map(r => r.penalty_source));
  const bySource = sources.map(source => {
    const rows = summary.filter(r => r.penalty_source === source);
    const pick = (status: string) => rows.filter(r => r.penalty_status === status).reduce((a, r) => a + asNumber(r.cnt), 0);
    return {
      source,
      total: rows.reduce((a, r) => a + asNumber(r.cnt), 0),
      amount: rows.reduce((a, r) => a + asNumber(r.amount), 0),
      confirmed: pick('confirmed'),
      waived: pick('waived'),
      pending: pick('pending'),
      stores: uniq(rows.map(r => r.store_code)).join(', '),
    };
  });

  // Trend 12 tháng: stacked bar theo nguồn.
  const trendMonths = uniq(trend.map(r => r.ym));
  const trendSets = uniq(trend.map(r => r.penalty_source)).map(source => {
    const byYm = new Map(trend.filter(r => r.penalty_source === source).map(r => [r.ym, asNumber(r.cnt)]));
    const ds = chartDataset(penaltySourceLabel(source), trendMonths.map(m => byYm.get(m) ?? 0), SOURCE_COLORS[source] || '#6b7280');
    return { ...ds, stack: 'src' } as typeof ds;
  });

  // Chat theo ngày 30d: cột khiếu nại mới + line SLA 24h.
  const dailyLabels = chatDaily.map(r => r.metric_date.slice(8));
  const dailySets = [
    chartDataset('Khiếu nại chat', chatDaily.map(r => asNumber(r.vol_total)), '#7c3aed'),
    { ...chartDataset('% xử lý trong 24h', chatDaily.map(r => r.sla_gate1_within_24h == null ? NaN : Math.round(asNumber(r.sla_gate1_within_24h) * 100)), '#059669', 'line', 'y1') },
  ];

  return (
    <>
      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Tháng</span>
          {months.map(m => (
            <button key={m} className={`tab ${month === m ? 'active' : ''}`} onClick={() => onMonth(m)}>{monthLabel(m)}</button>
          ))}
        </div>
      </section>

      <section className="card space" style={{ borderLeft: '4px solid var(--red)' }}>
        <div className="section-title">
          <h2>⏱ SLA đang chạy · live lúc {data?.meta?.refreshed_at ? new Date(data.meta.refreshed_at).toLocaleTimeString('vi-VN') : '-'}</h2>
          <span>Gate 1 chat: 24h từ cảnh báo · Giải trình: 3 ngày (Google/Zalo) / 7 ngày (Chat) · CS duyệt: 3 ngày từ lúc nộp. Bấm "Tải lại" để cập nhật đồng hồ.</span>
        </div>
        <div className="grid kpis" style={{ marginBottom: 14 }}>
          <KpiCard label="Chat chờ Gate 1" value={num(slaGate1.length)} sub={slaGate1.length ? `gấp nhất: ${fmtRemain(gate1Nearest)}` : 'không có ticket chờ'} tone={slaGate1.length ? remainTone(gate1Nearest) : 'green'} />
          <KpiCard label="Vỡ SLA chưa gán bộ phận" value={num(slaBreached.length)} sub="auto phạt 500K · CS cần gán" tone={slaBreached.length ? 'red' : 'green'} />
          <KpiCard label="Hạn giải trình đang mở" value={num(slaExplOpen.length)} sub={`${num(explOverdue)} phiếu đã quá hạn`} tone={explOverdue ? 'red' : slaExplOpen.length ? 'amber' : 'green'} />
          <KpiCard label="Giải trình chờ CS duyệt" value={num(slaReviewQueue.length)} sub="hạn duyệt 3 ngày" tone={slaReviewQueue.length ? 'amber' : 'green'} />
        </div>

        {slaGate1.length > 0 && (
          <>
            <div className="section-title"><h2>Chat chờ store xác nhận (Gate 1 · hạn 24h)</h2><span>{num(slaGate1.length)} ticket · gần hạn nhất lên đầu</span></div>
            <div className="scroll space">
              <table>
                <thead><tr>
                  <th>Ticket</th><th>Bộ phận</th><th>Mức độ</th><th>Chủ đề</th><th>Gửi cảnh báo</th><th>Hạn</th><th className="right">Còn lại</th>
                </tr></thead>
                <tbody>{slaGate1.map(r => (
                  <tr key={r.conv_analysis_id}>
                    <td className="tnum muted">…{r.conv_analysis_id.slice(-8)}</td>
                    <td><b>{r.bo_phan_lien_quan || <span className="muted">(chưa gán)</span>}</b></td>
                    <td><Tag tone={complaintLevelTone(r.complaint_level)}>{complaintLevelLabel(r.complaint_level)}</Tag></td>
                    <td className="need">{r.topic || '-'}</td>
                    <td className="tnum muted">{r.card_sent_at?.slice(5, 16)}</td>
                    <td className="tnum muted">{r.deadline?.slice(5) || '-'}</td>
                    <td className="right"><Tag tone={remainTone(r.remaining_h)}>{fmtRemain(r.remaining_h)}</Tag></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}

        {slaBreached.length > 0 && (
          <>
            <div className="section-title"><h2>Vỡ SLA 24h · CS chưa gán bộ phận chịu phạt</h2><span>{num(slaBreached.length)} ticket · đã auto phạt 500K (store trống)</span></div>
            <div className="scroll space">
              <table>
                <thead><tr><th>Ticket</th><th>Mức độ</th><th>Chủ đề</th><th>Gửi cảnh báo</th><th>Vỡ SLA lúc</th></tr></thead>
                <tbody>{slaBreached.map(r => (
                  <tr key={r.conv_analysis_id}>
                    <td className="tnum muted">…{r.conv_analysis_id.slice(-8)}</td>
                    <td><Tag tone={complaintLevelTone(r.complaint_level)}>{complaintLevelLabel(r.complaint_level)}</Tag></td>
                    <td className="need">{r.topic || '-'}</td>
                    <td className="tnum muted">{r.card_sent_at?.slice(5, 16) || '-'}</td>
                    <td className="tnum"><Tag tone="red">{r.sla_breached_at?.slice(5, 16)}</Tag></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}

        {slaExplOpen.length > 0 && (
          <>
            <div className="section-title"><h2>Hạn giải trình phiếu phạt đang mở</h2><span>{num(slaExplOpen.length)} phiếu chưa chốt store còn hành động · còn hạn lên đầu, quá hạn mới nhất kế tiếp · quá hạn = CS có thể chốt phạt</span></div>
            <div className="scroll space">
              <table>
                <thead><tr>
                  <th>Phiếu</th><th>Nguồn</th><th>Bộ phận</th><th>Trạng thái nộp</th><th>Tạo phiếu</th><th>Hạn giải trình</th><th className="right">Còn lại</th>
                </tr></thead>
                <tbody>{slaExplOpen.map(r => (
                  <tr key={r.id}>
                    <td className="tnum muted">#{r.id}</td>
                    <td><Tag tone={r.penalty_source === 'chat_complaint' ? 'violet' : r.penalty_source === 'zalo_review' ? 'cyan' : 'blue'}>{penaltySourceLabel(r.penalty_source)}</Tag></td>
                    <td><b>{r.store_code || <span className="muted">(chưa gán)</span>}</b></td>
                    <td>{r.dispute_status === 'rejected' ? <Tag tone="amber">Bị từ chối{r.penalty_source === 'chat_complaint' ? ' · nộp lại được' : ''}</Tag> : <Tag tone="gray">Chưa nộp</Tag>}</td>
                    <td className="tnum muted">{r.created_at?.replace('T', ' ').slice(5, 16)}</td>
                    <td className="tnum muted">{r.deadline?.slice(5) || '-'}</td>
                    <td className="right"><Tag tone={remainTone(r.remaining_h)}>{fmtRemain(r.remaining_h)}</Tag></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}

        {slaReviewQueue.length > 0 && (
          <>
            <div className="section-title"><h2>Giải trình chờ CS duyệt (hạn 3 ngày)</h2><span>{num(slaReviewQueue.length)} bản giải trình đã nộp</span></div>
            <div className="scroll">
              <table>
                <thead><tr>
                  <th>Loại</th><th>Phiếu</th><th>Bộ phận</th><th>Người nộp</th><th>Nộp lúc</th><th>Hạn duyệt</th><th className="right">Còn lại</th>
                </tr></thead>
                <tbody>{slaReviewQueue.map(r => (
                  <tr key={`${r.source}-${r.id}`}>
                    <td><Tag tone={r.source === 'tcdt' ? 'amber' : 'blue'}>{r.source === 'tcdt' ? 'TCĐT' : penaltySourceLabel(r.penalty_source)}</Tag></td>
                    <td className="tnum muted">#{r.penalty_id ?? r.violation_id ?? r.id}</td>
                    <td><b>{r.store_code || '-'}</b></td>
                    <td className="muted">{r.submitter_name || '-'}</td>
                    <td className="tnum muted">{r.created_at?.replace('T', ' ').slice(5, 16)}</td>
                    <td className="tnum muted">{r.deadline?.slice(5) || '-'}</td>
                    <td className="right"><Tag tone={remainTone(r.remaining_h)}>{fmtRemain(r.remaining_h)}</Tag></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}

        {slaGate1.length === 0 && slaBreached.length === 0 && slaExplOpen.length === 0 && slaReviewQueue.length === 0 && (
          <div className="card empty">Không có SLA nào đang chạy — mọi ticket đã xử lý xong. 🎉</div>
        )}
      </section>

      <section className="grid kpis">
        <KpiCard label="Phiếu phạt khiếu nại" value={num(total)} sub={`${monthLabel(month)} · Google + Zalo + Chat`} tone={total ? 'red' : 'green'} />
        <KpiCard label="Đã xác nhận phạt" value={num(confirmed)} sub={money(confirmedAmount)} tone={confirmed ? 'red' : 'green'} />
        <KpiCard label="Gỡ phạt (giải trình OK)" value={num(waived)} sub={total ? `${Math.round((100 * waived) / total)}% tổng phiếu` : 'không có phiếu'} tone="green" />
        <KpiCard label="Chờ xử lý" value={num(pending)} sub="chưa chốt phạt/gỡ" tone={pending ? 'amber' : 'green'} />
        <KpiCard label="SLA chat 24h" value={sla24 == null ? '-' : pct1(sla24)} sub={chatNow ? `${num(chatNow.vol_total)} khiếu nại chat` : 'chưa có snapshot'} tone={sla24 == null ? 'gray' : slaTone(sla24)} />
      </section>

      <section className="grid split atop space">
        <div className="card">
          <div className="section-title">
            <h2>Trend khiếu nại theo tháng · theo nguồn</h2>
            <span>Số phiếu phạt tạo trong tháng (theo ngày tạo phiếu)</span>
          </div>
          <div className="chart"><ChartBox labels={trendMonths.map(monthLabel)} datasets={trendSets} options={{ scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true } } }} /></div>
        </div>
        <div className="card">
          <div className="section-title">
            <h2>Theo nguồn · {monthLabel(month)}</h2>
            <span>Phạt / gỡ / chờ theo từng kênh khiếu nại</span>
          </div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Nguồn</th><th className="right">Phiếu</th><th className="right">Phạt</th>
                <th className="right">Gỡ</th><th className="right">Chờ</th><th className="right">Tiền phạt</th>
              </tr></thead>
              <tbody>{bySource.map(r => (
                <tr key={r.source}>
                  <td><b>{penaltySourceLabel(r.source)}</b><div className="muted">{r.stores}</div></td>
                  <td className="right tnum"><b>{num(r.total)}</b></td>
                  <td className="right tnum">{r.confirmed ? <Tag tone="red">{num(r.confirmed)}</Tag> : <span className="muted">0</span>}</td>
                  <td className="right tnum">{r.waived ? <Tag tone="green">{num(r.waived)}</Tag> : <span className="muted">0</span>}</td>
                  <td className="right tnum">{r.pending ? <Tag tone="amber">{num(r.pending)}</Tag> : <span className="muted">0</span>}</td>
                  <td className="right tnum">{money(r.amount)}</td>
                </tr>
              ))}
              {bySource.length === 0 && <tr><td colSpan={6} className="empty">Không có phiếu phạt trong {monthLabel(month)}.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid split atop space">
        <div className="card">
          <div className="section-title">
            <h2>Khiếu nại chat 30 ngày gần nhất</h2>
            <span>Cột = khiếu nại mới · đường = % store xác nhận trong 24h (SLA gate 1)</span>
          </div>
          {chatDaily.length === 0 ? (
            <div className="card empty">Chưa có snapshot chat theo ngày.</div>
          ) : (
            <div className="chart"><ChartBox labels={dailyLabels} datasets={dailySets} options={{ scales: { y: { beginAtZero: true }, y1: { position: 'right', beginAtZero: true, max: 100, grid: { display: false } } } }} /></div>
          )}
        </div>
        <div className="card">
          <div className="section-title">
            <h2>Chất lượng xử lý chat · {monthLabel(month)}</h2>
            <span>Theo bộ phận liên quan · GRR = gỡ ÷ (gỡ + phạt)</span>
          </div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Bộ phận</th><th className="right">Khiếu nại</th><th className="right">Là KN thật</th><th className="right">SLA 24h</th>
                <th className="right">Phạt</th><th className="right">Gỡ</th><th className="right">GRR</th>
              </tr></thead>
              <tbody>{chatDept.map(r => {
                const sla = r.sla_gate1_within_24h == null ? null : asNumber(r.sla_gate1_within_24h) * 100;
                const rr = r.good_resolution_rate == null ? null : asNumber(r.good_resolution_rate) * 100;
                return (
                  <tr key={r.store_code}>
                    <td><b>{r.store_code || '(chưa gán)'}</b></td>
                    <td className="right tnum">{num(r.vol_total)}</td>
                    <td className="right tnum">{num(r.gate1_yes)}</td>
                    <td className="right">{sla == null ? <span className="muted">-</span> : <Tag tone={slaTone(sla)}>{pct1(sla)}</Tag>}</td>
                    <td className="right tnum">{asNumber(r.pen_confirmed) ? <Tag tone="red">{num(r.pen_confirmed)}</Tag> : <span className="muted">0</span>}</td>
                    <td className="right tnum">{asNumber(r.pen_waived) ? <Tag tone="green">{num(r.pen_waived)}</Tag> : <span className="muted">0</span>}</td>
                    <td className="right">{rr == null ? <span className="muted">-</span> : <Tag tone={rr >= 50 ? 'green' : 'amber'}>{pct1(rr)}</Tag>}</td>
                  </tr>
                );
              })}
              {chatDept.length === 0 && <tr><td colSpan={7} className="empty">Chưa có snapshot chat cho {monthLabel(month)}.</td></tr>}
              </tbody>
            </table>
          </div>
          {grr != null && <div className="kpi-sub" style={{ marginTop: 8 }}>Toàn hệ thống: GRR {pct1(grr)} · CS kéo lại {num(chatNow?.cs_override_count)} override + {num(chatNow?.cs_reopen_count)} reopen</div>}
        </div>
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Chi tiết phiếu phạt · {monthLabel(month)}</h2>
          <span>{num(details.length)} phiếu gần nhất · kỳ KPI chốt tháng chính thức theo hệ thống ops-quality</span>
        </div>
        {details.length === 0 ? (
          <div className="card empty">Không có phiếu phạt trong {monthLabel(month)}.</div>
        ) : (
          <div className="scroll tall">
            <table>
              <thead><tr>
                <th>Ngày</th><th>Nguồn</th><th>Bộ phận</th><th className="right">Sao</th><th>Nội dung</th>
                <th className="right">Tiền</th><th>Trạng thái</th>
              </tr></thead>
              <tbody>{details.map(r => (
                <tr key={r.id}>
                  <td className="tnum muted">{r.day}</td>
                  <td><Tag tone={r.penalty_source === 'chat_complaint' ? 'violet' : r.penalty_source === 'zalo_review' ? 'cyan' : 'blue'}>{penaltySourceLabel(r.penalty_source)}</Tag></td>
                  <td><b>{r.store_code || <span className="muted">(chưa gán)</span>}</b></td>
                  <td className="right tnum">{r.review_star != null && asNumber(r.review_star) > 0 ? `${r.review_star}★` : <span className="muted">-</span>}</td>
                  <td className="need">{r.review_comment || <span className="muted">-</span>}</td>
                  <td className="right tnum">{money(r.penalty_amount)}</td>
                  <td><Tag tone={penaltyStatusTone(r.penalty_status)}>{penaltyStatusLabel(r.penalty_status)}</Tag></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
